import {
  BarChart3,
  Bell,
  Briefcase,
  Calculator,
  ClipboardList,
  Compass,
  FlaskConical,
  GraduationCap,
  LayoutDashboard,
  LineChart,
  LogOut,
  MessageSquare,
  Radar,
  Settings,
  Sliders,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth.js";
import { getAllowedBoards } from "../../lib/api.js";

/**
 * Navigation in Sinngruppen statt 16 flacher Einträge (Redesign 07.08.):
 * Führungskräfte finden ihre zwei Kernflächen ("Mein Bereich") sofort,
 * alles Weitere ist thematisch sortiert. Eine Gruppe erscheint nur, wenn
 * die Rolle mindestens eines ihrer Boards sehen darf.
 */
const NAV_GROUPS = [
  {
    label: "Mein Bereich",
    items: [
      { to: "/mein-team", icon: Users, key: "meinTeam", board: "mein-team" },
      { to: "/fk-dashboard", icon: Compass, key: "fkDashboard", board: "fk-dashboard" },
    ],
  },
  {
    label: "Retention",
    items: [
      { to: "/", icon: LayoutDashboard, key: "dashboard", board: "dashboard" },
      { to: "/retention", icon: Radar, key: "retention", board: "retention" },
      { to: "/onboarding", icon: GraduationCap, key: "onboarding", board: "onboarding" },
      { to: "/pulsumfrage", icon: ClipboardList, key: "pulsumfrage", board: "pulsumfrage" },
      { to: "/notifications", icon: Bell, key: "notifications", board: "notifications" },
    ],
  },
  {
    label: "Wirtschaft",
    items: [
      {
        to: "/wirtschaftlichkeit",
        icon: Calculator,
        key: "wirtschaftlichkeit",
        board: "wirtschaftlichkeit",
      },
    ],
  },
  {
    label: "Analysen",
    items: [
      { to: "/kpi-reporting", icon: BarChart3, key: "kpiReporting", board: "kpi-reporting" },
      { to: "/tenure", icon: LineChart, key: "tenure", board: "tenure" },
      { to: "/scenario", icon: Sliders, key: "scenario", board: "scenario" },
      {
        to: "/personalleiter",
        icon: Briefcase,
        key: "personalleiter",
        board: "personalleiter-cockpit",
      },
    ],
  },
  {
    label: "System",
    items: [
      { to: "/assistant", icon: MessageSquare, key: "assistant", board: "assistant" },
      {
        to: "/model-validation",
        icon: FlaskConical,
        key: "modelValidation",
        board: "model-validation",
      },
      { to: "/admin", icon: Settings, key: "admin", board: "user-admin" },
    ],
  },
] as const;

export function Sidebar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const [allowed, setAllowed] = useState<Set<string>>(new Set());

  useEffect(() => {
    getAllowedBoards()
      .then((boards) => setAllowed(new Set(boards)))
      .catch(() => setAllowed(new Set())); // on error: show nothing (safer than showing everything)
  }, []);

  const visibleGroups = NAV_GROUPS.map((group) => ({
    label: group.label,
    items: group.items.filter((item) => allowed.has(item.board)),
  })).filter((group) => group.items.length > 0);

  async function handleLogout() {
    await logout();
    navigate("/login");
  }

  return (
    <aside
      style={{
        borderRight: "1px solid var(--border)",
        background: "var(--bg-sidebar)",
      }}
      className="w-60 shrink-0 flex flex-col h-full"
    >
      {/* Logo */}
      <div className="px-5 py-5">
        <div className="flex items-center gap-3">
          <img
            src="/logo-quadrat.png"
            alt="mindsquare"
            className="w-9 h-9 shrink-0 rounded-lg"
            style={{ objectFit: "contain" }}
          />
          <div>
            <div
              className="text-[15px] font-bold leading-tight tracking-tight"
              style={{ color: "var(--text-primary)" }}
            >
              HR Analytics
            </div>
            <div
              className="text-[10px] leading-tight font-bold tracking-wide uppercase"
              style={{ color: "var(--accent)" }}
            >
              mindsquare AG
            </div>
          </div>
        </div>
      </div>

      {/* Nav */}
      <nav className="flex-1 pb-3 px-3 flex flex-col gap-4 overflow-y-auto">
        {visibleGroups.map((group) => (
          <div key={group.label}>
            <div
              className="px-3 pb-1.5 text-[10px] font-bold tracking-[0.14em] uppercase select-none"
              style={{ color: "var(--text-muted)" }}
            >
              {group.label}
            </div>
            <div className="flex flex-col gap-0.5">
              {group.items.map(({ to, icon: Icon, key }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === "/"}
                  className={({ isActive }) =>
                    `group flex items-center gap-3 px-3 py-2 rounded-[10px] text-[13px] transition-colors duration-150 ${
                      isActive
                        ? "font-bold"
                        : "font-normal hover:bg-[var(--bg-hover)] hover:text-[var(--text-primary)]"
                    }`
                  }
                  style={({ isActive }) => ({
                    background: isActive ? "var(--accent-dim)" : undefined,
                    color: isActive ? "var(--accent)" : "var(--text-secondary)",
                  })}
                >
                  {({ isActive }) => (
                    <>
                      <Icon
                        size={16}
                        strokeWidth={isActive ? 2.4 : 2}
                        className="shrink-0 transition-transform duration-150 group-hover:scale-110"
                      />
                      {t(`nav.${key}`)}
                    </>
                  )}
                </NavLink>
              ))}
            </div>
          </div>
        ))}
      </nav>

      {/* Logout */}
      <div className="px-3 pb-4 pt-3" style={{ borderTop: "1px solid var(--border)" }}>
        <button
          type="button"
          onClick={handleLogout}
          className="w-full flex items-center gap-3 px-3 py-2 rounded-[10px] text-[13px] transition-colors duration-150 hover:bg-[var(--bg-hover)] hover:text-[var(--text-secondary)]"
          style={{ color: "var(--text-muted)" }}
        >
          <LogOut size={15} />
          {t("nav.logout")}
        </button>
      </div>
    </aside>
  );
}
